'use client'

import type { ConstraintInstanceType } from '@/portfolio/shared/constraint/types'
import type { Position } from '@/types/api/accounts'
import type { SampleHoldings } from '@/types/api/models'

import { useMemo } from 'react'

import { ComplianceItem } from '@/components/molecules/compliance-item'
import { Constraint } from '@/components/molecules/constraint'
import { useGetFilterListsMap } from '@/hooks/use-get-filter-lists-map'
import { indexTierPieces } from '@/portfolio/shared/constraint/equal-weight'
import {
  isEqualWeightValidation,
  ModelConstraintType,
  type ModelConstraintValidation,
  type PiecewiseConstraintValidation,
} from '@/types/api/constraints'

import {
  CONSTRAINT_CONFIGS,
  getConstraintItems,
  getEqualWeightItems,
} from './helpers'
import {
  ComplianceListContainer,
  ItemsContainer,
  TitleContainer,
} from './styles'

interface ComplianceListProps {
  className?: string
  constraints?: ModelConstraintValidation[]
  holdings?: Holding[]
  constraintContainerClassName?: string
  multiplyBy100?: boolean
  constraintInstanceType?: ConstraintInstanceType
}

export type Holding = SampleHoldings | Position

function enrichViolationsWithHoldings(
  violations:
    | { isin: string; description: string; weight: number; note?: string }[]
    | undefined,
  holdings: Holding[],
) {
  if (!violations) return undefined

  const holdingsMap = new Map(
    holdings.map((holding) => [holding.isin, holding]),
  )

  return violations.map((violation) => {
    const holding = holdingsMap.get(violation.isin)
    return {
      ...violation,
      description: violation.description || holding?.description || '',
      weight: violation.weight || holding?.weight || 0,
    }
  })
}

export function ComplianceList({
  className,
  constraints,
  holdings,
  constraintContainerClassName,
  // TODO: Threading this prop to avoid multiplying by 100 on proposal compliance (Refactor and remove this prop)
  multiplyBy100 = true,
  constraintInstanceType,
}: ComplianceListProps) {
  const { filterListsMap } = useGetFilterListsMap()

  const groupedConstraints = useMemo(() => {
    if (!constraints) return []

    const grouped = constraints.reduce(
      (acc, constraint: ModelConstraintValidation) => {
        const type = constraint.constraint.type
        if (!acc[type]) {
          acc[type] = []
        }

        const isPiecewise = type === ModelConstraintType.Piecewise
        if (isPiecewise) {
          const additionalConstraints = (
            constraint as PiecewiseConstraintValidation
          ).additional_constraints
          additionalConstraints.forEach(
            (additionalConstraint: ModelConstraintValidation) => {
              const additionalConstraintType =
                additionalConstraint.constraint.type
              if (!acc[additionalConstraintType]) {
                acc[additionalConstraintType] = []
              }

              acc[additionalConstraintType].push(additionalConstraint)
            },
          )
          return acc
        }

        const isLadderPolicy = type === ModelConstraintType.LadderPolicy
        // making sure we only add one ladder policy constraint
        // shouldn't be needed but it seems like the llm can, improperly,
        // return multiple ladder policy constraints
        const shouldAddConstraint = !isLadderPolicy || acc[type].length === 0
        if (shouldAddConstraint) {
          acc[type].push(constraint)
        }

        return acc
      },
      {} as Record<ModelConstraintType, typeof constraints>,
    )

    return Object.entries(grouped)
  }, [constraints])

  const tierPieces = useMemo(
    () => indexTierPieces((constraints ?? []).map((c) => c.constraint)),
    [constraints],
  )

  if (!constraints) return null

  return (
    <ComplianceListContainer className={className}>
      {groupedConstraints.map(([type, constraints]) => {
        const config = CONSTRAINT_CONFIGS.find((c) => c.type === type)
        if (!config) return null

        // Combine all items from constraints of the same type
        const allItems = constraints.flatMap((constraint) =>
          isEqualWeightValidation(constraint)
            ? getEqualWeightItems({
                validation: constraint,
                piece: tierPieces.get(constraint.constraint.id ?? ''),
              })
            : getConstraintItems(
                constraint,
                holdings,
                multiplyBy100,
                filterListsMap,
                constraintInstanceType,
              ),
        )
        if (allItems.length === 0) return null

        // Check if any constraint in the group is non-compliant
        const hasError = constraints.some((constraint) => !constraint.compliant)

        return (
          <Constraint
            key={config.titleText}
            title={config.titleText}
            displayTitle={
              <TitleContainer hasError={hasError}>
                {config.titleText}
              </TitleContainer>
            }
            icon={config.icon}
            iconColor={config.iconColor}
            iconBackgroundColor={config.iconBackgroundColor}
            itemClassName="px-4"
            containerClassName={constraintContainerClassName}
          >
            <ItemsContainer>
              {allItems.map((item, index) => {
                // Skip violations for date range items, otherwise enrich violations with holdings data
                const itemViolations =
                  item.label === 'Date range'
                    ? []
                    : enrichViolationsWithHoldings(
                        item.violations,
                        holdings || [],
                      )

                return (
                  <ComplianceItem
                    key={`${item.label}-${index}`}
                    label={item.label}
                    value={item.value}
                    type={type as ModelConstraintType}
                    status={item.status}
                    hasViolations={!!item.violationsCount}
                    violationCount={item.violationsCount}
                    violations={itemViolations}
                    isLast={index === allItems.length - 1}
                    hidePopout={type === ModelConstraintType.Diversification}
                    isMarketValueConstraint={item.isMarketValueConstraint}
                  />
                )
              })}
            </ItemsContainer>
          </Constraint>
        )
      })}
    </ComplianceListContainer>
  )
}
